import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const PWAInstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }

    // Check device type
    const userAgent = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroidDevice = /Android/.test(userAgent);
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Listen for install prompt (Chrome/Edge on Android and desktop)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  if (isInstalled) {
    return (
      <Button variant="outline" size="sm" disabled className="gap-2">
        <Icon name="check" size={16} />
        Installed
      </Button>
    );
  }

  // If native prompt is available (Chrome/Edge), use it directly
  if (deferredPrompt) {
    return (
      <Button variant="outline" size="sm" onClick={handleInstall} className="gap-2">
        <Icon name="download" size={16} />
        Install App
      </Button>
    );
  }

  // Otherwise, show manual instructions dialog
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Icon name="smartphone" size={16} />
          Install App
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Install Admin App</DialogTitle>
          <DialogDescription>
            Install this app to your device for quick access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          {isIOS ? (
            <div className="space-y-2">
              <p className="font-medium">On iOS (Safari):</p>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Tap the <strong>Share</strong> button (square with arrow)</li>
                <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                <li>Tap <strong>"Add"</strong> in the top right</li>
              </ol>
            </div>
          ) : isAndroid ? (
            <div className="space-y-2">
              <p className="font-medium">On Android (Chrome):</p>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Tap the <strong>menu</strong> button (3 dots)</li>
                <li>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></li>
                <li>Tap <strong>"Add"</strong> to confirm</li>
              </ol>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="font-medium">On Desktop (Chrome/Edge):</p>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>Look for the <strong>install icon</strong> in the address bar</li>
                <li>Or click the <strong>menu</strong> (3 dots) → <strong>"Install..."</strong></li>
                <li>Click <strong>"Install"</strong> to confirm</li>
              </ol>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
